import React, {memo, ReactNode, useCallback, useEffect, useRef} from 'react';

import {Button} from '@mui/material';
import classNames from 'classnames';
import {motion, useAnimationControls} from 'framer-motion';

import {APP_PROGRESS_BUTTON_ANIMATION} from './constants';
import './styles';

interface AppProgressButtonProps {
  label?: string | ReactNode;
  disabled?: boolean;
  icon?: ReactNode;
  isTextLeft?: boolean;
  isAuto?: boolean;
  progressDurationMs?: number;
  id?: string;
  onProgressEnds?: () => void;
  onPress?: () => void;
}

export const AppProgressButton: React.FC<AppProgressButtonProps> = ({
  label,
  disabled = false,
  icon,
  isTextLeft = true,
  isAuto = false,
  progressDurationMs = APP_PROGRESS_BUTTON_ANIMATION.DEFAULT_PROGRESS_DURATION_MS,
  id = 'app-progress-button',
  onProgressEnds,
  onPress,
}) => {
  //#region [---- [STATE & REFS] ----]
  const controls = useAnimationControls();
  const isHandledAnimationEndsRef = useRef(false);
  const isAnimationStarted = useRef(false);
  //#endregion

  //#region [---- [LOGIC] ----]
  const handlePress = useCallback(() => {
    onPress?.();
  }, [onPress]);

  const startAnimation = useCallback(() => {
    if (isAnimationStarted.current) return;
    isAnimationStarted.current = true;

    controls
      .start({
        width: APP_PROGRESS_BUTTON_ANIMATION.FINAL_PROGRESS_WIDTH,
        transition: {
          duration:
            progressDurationMs /
            APP_PROGRESS_BUTTON_ANIMATION.MILLISECONDS_PER_SECOND,
          ease: APP_PROGRESS_BUTTON_ANIMATION.EASE,
        },
      })
      .then(() => {
        if (!isHandledAnimationEndsRef.current) {
          isHandledAnimationEndsRef.current = true;
          onProgressEnds?.();
        }
      });
  }, [controls, onProgressEnds, progressDurationMs]);
  //#endregion

  //#region [---- [EFFECTS] ----]
  useEffect(() => {
    if (!isAnimationStarted.current) {
      startAnimation();
    }
  }, [startAnimation]);
  //#endregion

  return (
    <Button
      id={id}
      className={classNames('app-progress-button', {
        'app-progress-button--disabled': disabled,
        'app-progress-button--auto': isAuto,
      })}
      onClick={handlePress}
      disabled={disabled}>
      <motion.div
        className="app-progress-button__progress"
        animate={controls}
        initial={{width: APP_PROGRESS_BUTTON_ANIMATION.INITIAL_PROGRESS_WIDTH}}
      />

      <div className="app-progress-button__content">
        {isTextLeft && (
          <span className="app-progress-button__label">{label}</span>
        )}
        {icon && <span className="app-progress-button__icon">{icon}</span>}
        {!isTextLeft && (
          <span className="app-progress-button__label">{label}</span>
        )}
      </div>
    </Button>
  );
};

export const ProgressButton = memo(AppProgressButton);
